import { Alert, Button, Card, Input, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Activity = {
  id: string;
  code: string;
  name: string;
  capacity?: number | null;
  fee_amount?: number | null;
  active_members?: number;
  waitlisted_members?: number;
  session_name?: string;
};

type Membership = {
  id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  member_role: string;
  status: string;
  joined_on: string;
  ended_on?: string | null;
};

export default function ActivityMembersPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.activity.manage') || user?.permissions?.includes('education.admin')
  );
  const canDemand = Boolean(
    user?.permissions?.includes('education.fee.demand') || user?.permissions?.includes('education.admin')
  );
  const [periodKey, setPeriodKey] = useState(() => {
    const d = new Date();
    return `${d.getUTCFullYear()}-Q${Math.floor(d.getUTCMonth() / 3) + 1}`;
  });

  const activity = useQuery({
    queryKey: ['education-activity', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get<Activity>(`/education/activities/${id}`);
      return res.data;
    },
  });

  const members = useQuery({
    queryKey: ['education-activity-memberships', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get<Membership[]>('/education/activity-memberships', {
        params: { activity_id: id },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const endMembership = useMutation({
    mutationFn: (membershipId: string) => api.post(`/education/activity-memberships/${membershipId}/end`),
    onSuccess: () => {
      message.success(t('activityMembershipEnded'));
      queryClient.invalidateQueries({ queryKey: ['education-activity-memberships', id] });
      queryClient.invalidateQueries({ queryKey: ['education-activity', id] });
      queryClient.invalidateQueries({ queryKey: ['education-activities'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const raiseFees = useMutation({
    mutationFn: () =>
      api.post('/education/activities/fee-demands/generate', {
        period_key: periodKey,
        activity_id: id,
      }),
    onSuccess: (res) => {
      const body = res.data as { created?: number; reused?: number; skipped?: number; members?: number };
      message.success(
        t('activityFeeDemandsGenerated', {
          created: body.created ?? 0,
          reused: body.reused ?? 0,
          skipped: body.skipped ?? 0,
          members: body.members ?? 0,
        })
      );
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (activity.isError) {
    return (
      <EducationPageChrome title={t('members')}>
        <Alert type="error" showIcon message={t('unavailable', { status: 404 })} />
      </EducationPageChrome>
    );
  }

  const row = activity.data;

  return (
    <EducationPageChrome
      title={row ? `${t('members')}: ${row.code}` : t('members')}
      description={row ? `${row.name}${row.session_name ? ` — ${row.session_name}` : ''}` : t('activityMembershipsIntro')}
      extra={
        <Space wrap>
          {canManage ? (
            <Button
              type="primary"
              onClick={() => navigate(`/education/activities/memberships/new?activity_id=${id}`)}
            >
              {t('enrollActivityMember')}
            </Button>
          ) : null}
          {canManage ? (
            <Button onClick={() => navigate(`/education/activities/memberships/bulk?activity_id=${id}`)}>
              {t('bulkActivityEnrollment')}
            </Button>
          ) : null}
          {canManage ? (
            <Button onClick={() => navigate(`/education/activities/${id}/edit`)}>{t('editActivity')}</Button>
          ) : null}
          <Button onClick={() => navigate(`/education/activities/meetings?activity_id=${id}`)}>
            {t('activityMeetings')}
          </Button>
          <Button onClick={() => navigate('/education/activities/list')}>{t('backToActivityCatalogue')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {row ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('members')}: {Number(row.active_members ?? 0)}
            {row.capacity != null ? ` / ${row.capacity}` : ''} · {t('waitlisted')}:{' '}
            {Number(row.waitlisted_members ?? 0)}
            {row.fee_amount != null ? ` · ${t('fee')}: ${Number(row.fee_amount)}` : ''}
          </Typography.Paragraph>
        ) : null}

        {canManage && canDemand && row?.fee_amount != null && Number(row.fee_amount) > 0 ? (
          <Card size="small" title={t('raiseActivityFeeDemands')}>
            <Space wrap>
              <Input
                value={periodKey}
                onChange={(e) => setPeriodKey(e.target.value)}
                placeholder={t('periodKey')}
                style={{ width: 160 }}
              />
              <Button type="primary" loading={raiseFees.isPending} onClick={() => raiseFees.mutate()}>
                {t('raiseActivityFeeDemands')}
              </Button>
            </Space>
            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              {t('raiseActivityFeeDemandsHint')}
            </Typography.Paragraph>
          </Card>
        ) : null}

        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={members.isLoading || activity.isLoading}
            dataSource={members.data ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: t('student'),
                render: (_: unknown, m: Membership) =>
                  `${m.admission_no || ''} — ${[m.first_name, m.last_name].filter(Boolean).join(' ')}`,
              },
              { title: t('memberRole'), dataIndex: 'member_role', width: 100 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (v: string) => (
                  <Tag color={v === 'active' ? 'success' : v === 'waitlisted' ? 'warning' : undefined}>{v}</Tag>
                ),
              },
              { title: t('joinedOn'), dataIndex: 'joined_on', width: 120 },
              {
                title: t('actions'),
                width: 120,
                render: (_: unknown, m: Membership) =>
                  canManage && (m.status === 'active' || m.status === 'waitlisted') ? (
                    <Popconfirm title={t('activityMembershipEndConfirm')} onConfirm={() => endMembership.mutate(m.id)}>
                      <Button size="small" loading={endMembership.isPending}>
                        {t('endMembership')}
                      </Button>
                    </Popconfirm>
                  ) : null,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
